import type { StatsigUser } from '../config/statsig';

// Helper function to safely access global Statsig instance
export const getStatsig = () => {
  if (typeof window !== 'undefined' && (window as any).statsig) {
    return (window as any).statsig;
  }
  return null;
};

// Event tracking utilities - these should be used within React components with useStatsigClient
export const createEventTracker = (statsigClient: any) => ({
  trackEvent: (eventName: string, value?: string | number, metadata?: Record<string, any>) => {
    try {
      if (statsigClient) {
        statsigClient.logEvent(eventName, value, metadata || {});
      }
    } catch (error) {
      console.warn('Failed to track event:', eventName, error);
    }
  }
});

// Career Card Events
export const trackCareerCardEvent = (statsigClient: any, action: 'created' | 'updated' | 'deleted' | 'viewed', cardData: {
  cardId: string;
  cardType: string;
  tags?: string[];
  impactLevel?: number;
  engagementLevel?: number;
}) => {
  try {
    if (statsigClient) {
      statsigClient.logEvent(`career_card_${action}`, cardData.impactLevel, {
        card_id: cardData.cardId,
        card_type: cardData.cardType,
        tags: cardData.tags,
        engagement_level: cardData.engagementLevel,
        timestamp: new Date().toISOString()
      });
    }
  } catch (error) {
    console.warn('Failed to track career card event:', error);
  }
};

// Note Events
export const trackNoteEvent = (statsigClient: any, action: 'created' | 'updated' | 'deleted' | 'viewed', noteData: {
  noteId: string;
  tags?: string[];
  contentLength?: number;
}) => {
  try {
    if (statsigClient) {
      statsigClient.logEvent(`note_${action}`, noteData.contentLength, {
        note_id: noteData.noteId,
        tags: noteData.tags,
        timestamp: new Date().toISOString()
      });
    }
  } catch (error) {
    console.warn('Failed to track note event:', error);
  }
};

// Review Events
export const trackReviewEvent = (statsigClient: any, action: 'generated' | 'viewed' | 'exported', reviewData: {
  reviewId: string;
  reviewType?: string;
  wordCount?: number;
  selectedItemsCount?: number;
}) => {
  try {
    if (statsigClient) {
      statsigClient.logEvent(`review_${action}`, reviewData.wordCount, {
        review_id: reviewData.reviewId,
        review_type: reviewData.reviewType,
        selected_items_count: reviewData.selectedItemsCount,
        timestamp: new Date().toISOString()
      });
    }
  } catch (error) {
    console.warn('Failed to track review event:', error);
  }
};

// Feature Usage Events
export const trackFeatureUsage = (statsigClient: any, featureName: string, metadata?: Record<string, any>) => {
  try {
    if (statsigClient) {
      statsigClient.logEvent('feature_accessed', undefined, {
        feature_name: featureName,
        ...metadata,
        timestamp: new Date().toISOString()
      });
    }
  } catch (error) {
    console.warn('Failed to track feature usage:', error);
  }
};

// File Upload Events
export const trackFileUpload = (statsigClient: any, fileData: {
  fileType: string;
  fileSize: number;
  uploadSource: 'local' | 'google_drive';
  processingType?: string;
}) => {
  try {
    if (statsigClient) {
      statsigClient.logEvent('file_uploaded', fileData.fileSize, {
        file_type: fileData.fileType,
        upload_source: fileData.uploadSource,
        processing_type: fileData.processingType,
        timestamp: new Date().toISOString()
      });
    }
  } catch (error) {
    console.warn('Failed to track file upload:', error);
  }
};

// Search Events
export const trackSearch = (statsigClient: any, searchData: {
  searchType: 'notes' | 'cards' | 'files' | 'global';
  query: string;
  resultsCount: number;
  filters?: string[];
}) => {
  try {
    if (statsigClient) {
      statsigClient.logEvent('search_performed', searchData.resultsCount, {
        search_type: searchData.searchType,
        query_length: searchData.query.length,
        filters: searchData.filters,
        timestamp: new Date().toISOString()
      });
    }
  } catch (error) {
    console.warn('Failed to track search:', error);
  }
};

// Onboarding Events
export const trackOnboardingStep = (statsigClient: any, step: string, completed: boolean, metadata?: Record<string, any>) => {
  try {
    if (statsigClient) {
      statsigClient.logEvent('onboarding_step', undefined, {
        step,
        completed,
        ...metadata,
        timestamp: new Date().toISOString()
      });
    }
  } catch (error) {
    console.warn('Failed to track onboarding step:', error);
  }
};

export const trackOnboardingCompletion = (statsigClient: any, metadata?: Record<string, any>) => {
  try {
    if (statsigClient) {
      statsigClient.logEvent('onboarding_completed', undefined, {
        ...metadata,
        timestamp: new Date().toISOString()
      });
    }
  } catch (error) {
    console.warn('Failed to track onboarding completion:', error);
  }
};

// Error tracking
export const trackError = (statsigClient: any, errorData: {
  errorType: string;
  errorMessage: string;
  component?: string;
  userId?: string;
  metadata?: Record<string, any>;
}) => {
  try {
    if (statsigClient) {
      statsigClient.logEvent('error_occurred', undefined, {
        error_type: errorData.errorType,
        error_message: errorData.errorMessage,
        component: errorData.component,
        user_id: errorData.userId,
        ...errorData.metadata,
        timestamp: new Date().toISOString()
      });
    }
  } catch (error) {
    console.warn('Failed to track error:', error);
  }
};

// Performance tracking
export const trackPerformance = (statsigClient: any, performanceData: {
  action: string;
  duration: number;
  component?: string;
  metadata?: Record<string, any>;
}) => {
  try {
    if (statsigClient) {
      statsigClient.logEvent('performance_metric', performanceData.duration, {
        action: performanceData.action,
        component: performanceData.component,
        ...performanceData.metadata,
        timestamp: new Date().toISOString()
      });
    }
  } catch (error) {
    console.warn('Failed to track performance:', error);
  }
};

// User activity tracking
export const trackUserActivity = (statsigClient: any, activityData: {
  activityType: 'session_start' | 'session_end' | 'page_view' | 'interaction';
  page?: string;
  duration?: number;
  metadata?: Record<string, any>;
}) => {
  try {
    if (statsigClient) {
      statsigClient.logEvent('user_activity', activityData.duration, {
        activity_type: activityData.activityType,
        page: activityData.page,
        ...activityData.metadata,
        timestamp: new Date().toISOString()
      });
    }
  } catch (error) {
    console.warn('Failed to track user activity:', error);
  }
};

// Utility to safely get user properties for Statsig
export const getStatsigUserProperties = (user: StatsigUser | null) => {
  if (!user) return {};
  
  return {
    userID: user.userID,
    email: user.email,
    custom: user.custom || {},
    privateAttributes: user.privateAttributes || {}
  };
}; 